// src/components/legal/LegalDocShell.js
//
// Shared on-brand chrome for the public legal pages (/terms, /privacy, /dpa).
// Reproduces the source documents' chrome (PH-flag topbar, Sahod brand header,
// mini cross-link footer) and renders the "Version X · Effective …" line from
// src/config/legal.js so a document bump is a single config edit (C3, C4).
//
// See PRD/PRD_LegalConsent.md.
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { LEGAL_VERSIONS, LEGAL_EFFECTIVE, formatEffective } from "../../config/legal";
import "../../styles/sections/legal.css";

const CROSS_LINKS = [
  { to: "/terms", label: "Terms of Service" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/dpa", label: "Data Processing Agreement" },
];

/**
 * @param {object} props
 * @param {"terms"|"privacy"|"dpa"} props.docKey  Which document (drives version/date line).
 * @param {string} props.title        Page title (also sets document.title).
 * @param {string} props.description   Meta-description for crawlers.
 * @param {React.ReactNode} props.children  The document body.
 */
export const LegalDocShell = ({ docKey, title, description, children }) => {
  const version = LEGAL_VERSIONS[docKey];
  const effective = formatEffective(LEGAL_EFFECTIVE[docKey]);

  // CRA has no SSR, so set the title/description client-side for crawlers.
  // (Scroll-to-top on navigate is handled globally by <ScrollToTop> in App.js.)
  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${title} — Sahod`;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta && description) meta.setAttribute("content", description);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc != null) meta.setAttribute("content", prevDesc);
    };
  }, [title, description]);

  return (
    <div className="legal-doc">
      <div className="legal-doc__topbar" />
      <header className="legal-doc__site-header">
        <Link to="/" className="legal-doc__brand">
          <span className="legal-doc__brand-dot" />
          Sahod
        </Link>
      </header>

      <main className="legal-doc__main">
        <h1 className="legal-doc__title">{title}</h1>
        <p className="legal-doc__meta">
          Version {version} &middot; Effective {effective}
        </p>
        {children}
      </main>

      <footer className="legal-doc__footer">
        &copy; 2026 Sahod.Solutions.
        {CROSS_LINKS.filter((l) => l.to !== `/${docKey}`).map((l) => (
          <span key={l.to}>
            {"  |  "}
            <Link to={l.to}>{l.label}</Link>
          </span>
        ))}
      </footer>
    </div>
  );
};
